import { Component, type ErrorInfo, type ReactNode } from 'react'

import { i18n } from '@/shared/i18n'
import { Button } from '@/shared/ui/button'

type ErrorBoundaryProps = {
  children: ReactNode
  /** Rendered instead of the subtree. Receives a reset that retries the render. */
  fallback?: (error: Error, reset: () => void) => ReactNode
  onError?: (error: Error, info: ErrorInfo) => void
}

type ErrorBoundaryState = { error: Error | null }

/**
 * Catches exceptions thrown while rendering. Data that failed to load is not
 * this — that is ordinary Query state rendered as an error block.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Log the error and where it happened — never the props that caused it,
    // which routinely carry personal data.
    console.error('render error', error.message, info.componentStack)
    this.props.onError?.(error, info)
  }

  reset = () => this.setState({ error: null })

  override render() {
    const { error } = this.state
    if (error === null) return this.props.children

    if (this.props.fallback !== undefined) return this.props.fallback(error, this.reset)

    // `i18n.t`, not the hook — this is a class component, and an error screen
    // does not need to live-switch languages.
    return (
      <div role="alert" className="p-8">
        <p className="font-medium">{i18n.t('common:errors.renderTitle')}</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={this.reset}>
          {i18n.t('common:actions.retry')}
        </Button>
      </div>
    )
  }
}

/**
 * After a deploy the previous build's chunks are gone from the CDN while a tab
 * is still open, so a lazy import rejects. Blanking the screen is not an
 * option — offer the reload that fixes it.
 */
export function ChunkErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(error, reset) =>
        /chunk|dynamically imported module|Importing a module script failed/i.test(error.message) ? (
          <div role="alert" className="p-8">
            <p className="font-medium">{i18n.t('common:errors.chunkTitle')}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {i18n.t('common:errors.chunkMessage')}
            </p>
            <Button size="sm" className="mt-3" onClick={() => globalThis.location.reload()}>
              {i18n.t('common:actions.reload')}
            </Button>
          </div>
        ) : (
          <div role="alert" className="p-8">
            <p className="font-medium">{i18n.t('common:errors.renderTitle')}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={reset}>
              {i18n.t('common:actions.retry')}
            </Button>
          </div>
        )
      }
    >
      {children}
    </ErrorBoundary>
  )
}
